/** @typedef {import('headlight-configurable-application').Pict} Pict */

/**
 * @typedef {Object|Array<String>} Payload
 * @property {String} [QRCode] - QRCode value of the item scanned.
 */
/**
 * @typedef {Object} QREvent
 * @property {Payload} payload - payload for the event.
 * @property {String} event - the action for the ui to consume from the event.
 */

/**
 * Entities whose "Printable" manifests describe a QR label layout.
 */
const _PrintableManifestEntities = [ 'Sample', 'Location' ];

/**
 * The product that OWNS the printable manifests (`LIMS-Sample-Printable` et al).
 *
 * They are LIMS configuration, not per-consumer configuration: a form document, the panels and the LIMS
 * app all print the same customer's label. Left unset the loader would look them up under the hosting
 * app's own product (e.g. `HeadlightDynamicForm-Sample-Printable`), find nothing, and silently fall back
 * to the built-in layout. Duplicating the manifests per product would mean keeping N copies of one
 * customer's label in sync, so the product is named here instead.
 */
const _PrintableManifestProduct = 'LIMS';

const libManifestPackagingService = require('manifest-packaging-service');

const qrTranslator = require('../../lib/qrcode-management/source/qr-translation.js');

const PictProvider = require('pict-provider');

const busLib = require('../../lib/qrcode-management/source/QRBus.js');

// Inline data URI for the HeadLight logo so QR code generation works in
// offline (iOS WKWebView) contexts where /images/ paths are unavailable.
//TODO: this is to support offline but is a bit hard to support - see if we can make this more robust
const HEADLIGHT_LOGO_BLACK_DATA_URI = 'data:image/png;base64,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';

class Dialog
{
	/**
	 *
	 * @param {Object} options
	 * @param {Object} options.body - The body of the dialog, can be a string or an HTMLElement
	 * @param {boolean} [options.closeOnBackdropClick=true] - Whether the dialog should close when the backdrop is clicked
	 */
	constructor(options)
	{
		this._dialog = document.createElement('div');
		Object.assign(this._dialog.style, {
			display: 'none',
			position: 'fixed',
			left: '50%',
			marginLeft: '-150px',
			width: '300px',
			top: '100px',
			zIndex: 1001,
			background: 'white',
		});

		this._dropShadow = document.createElement('div');
		Object.assign(this._dropShadow.style, {
			display: 'none',
			position: 'fixed',
			left: '0',
			top: '0',
			right: '0',
			bottom: '0',
			zIndex: 1000,
			backdropFilter: 'grayscale(1) blur(3px)',
			background: '#333333ba',
		});
		document.body.appendChild(this._dialog);
		document.body.appendChild(this._dropShadow);

		if (options.closeOnBackdropClick !== false)
		{
			this._dropShadow.addEventListener('click', () => this.hide());
		}

		if (typeof options.body === 'string')
		{
			this._dialog.innerHTML = options.body;
		}
		else if (options.body instanceof HTMLElement)
		{
			this._dialog.appendChild(options.body);
		}
		else
		{
			throw new Error('Unsupported options.body type: ' + typeof options.body);
		}
	}

	/**
	 * Locks scroll while dialog is open
	 * @return {void}
	 */
	lockScroll()
	{
		if (document.body.getAttribute('scroll-locked') === 'true')
		{
			return;
		}
		document.body.setAttribute('scroll-locked', 'true');
		document.body.style.overflow = 'hidden';
		document.body.style.position = 'relative';
	}

	/**
	 * Unlocks scroll when dialog is closed.
	 */
	unlockScroll()
	{
		document.body.removeAttribute('scroll-locked');
		document.body.style.overflow = '';
		document.body.style.position = '';
	}

	/**
	 * Shows the popup dialog.
	 */
	show()
	{
		this._dialog.style.display = 'block';
		this._dropShadow.style.display = 'block';
		this.lockScroll();
	}

	/**
	 * Hides the popup dialog.
	 */
	hide()
	{
		this._dialog.style.display = 'none';
		this._dropShadow.style.display = 'none';
		this.unlockScroll();
	}

	/**
	 * Destroys the popup dialog.
	 */
	destroy()
	{
		if (this._dialog.parentElement)
		{
			this._dialog.parentElement.removeChild(this._dialog);
		}
	}
}

// Documentation: https://scanapp.org/html5-qrcode-docs/docs/apis

class QRCodeProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		this._dialog;
		this._qrCodeScanner;
		this._hiddenLayer;
		this._canvas;
		this._qrCodeContainer;
		this._printFrame;

		this.typeConfig = {};

		this.capturedQRCode = '';
		this.scannerTimer = Date.now();
		this.readerActive = false;
		this.globalHandler = (event) =>
		{};
		if (pOptions.globalHandler)
		{
			this.globalHandler = pOptions.globalHandler;
		}
		this.handler = this.globalHandler;

		this.printStyle = pOptions.printStyle || 'thin';
		this.customerLogo = null;
		this.getHeadlightLogo = null;

		this.lastCameraEvent =
		{
			time: Date.now(),
			content: '',
		};

		this.cameraKey = '';

		// Uncomment the block below to test the timing of scanning/keyboard events.
		/*
		window.logger = [];
		window.called = false;
		window.calledStart = 0;
		window.elapsed = 0;
		this.perfTimer = performance.now();
		*/

		this.QRBus = new busLib.QRBus({
			eventHandler: this.passEvent,
			getByQRCode: this.getByQRCode,
		});

		document.addEventListener('keypress', this.scannerWatcher);
		this.logosInitialized = false;
	}

	onAfterInitializeAsync(fCallback)
	{
		super.onAfterInitializeAsync((pError) =>
		{
			this.initializeLogos().finally(() => fCallback(pError));
		});
	}

	onBeforeLoadDataAsync(fCallback)
	{
		super.onBeforeLoadDataAsync((pError) =>
		{
			// The logos are only needed when a label prints; start loading them but do not hold up the document.
			this.initializeLogos();
			return fCallback(pError);
		});
	}

	onInitialize()
	{
		if (this.pict.providers.SessionProvider)
		{
			this.pict.providers.SessionProvider.onSessionChange((pNewSession) =>
			{
				delete this.headlightLogo;
				delete this.customerLogo;
				delete this.logosInitialized;
				delete this._logosPromise;
			});
		}
		return super.onInitialize();
	}

	/**
	 * Load the HeadLight and customer logos used on printed labels. Safe to call repeatedly: one load
	 * is shared, and the print paths await it so a label never draws before the logos arrive.
	 * @return {Promise<void>}
	 */
	initializeLogos()
	{
		if (!this.pict.PictApplication.loggedIn || this.logosInitialized)
		{
			return Promise.resolve();
		}
		if (this._logosPromise)
		{
			return this._logosPromise;
		}
		this.headlightLogo = new Image();
		this.headlightLogo.src = HEADLIGHT_LOGO_BLACK_DATA_URI;
		this._logosPromise = this.getCustomerLogo().then((res) =>
		{
			if (res)
			{
				this.customerLogo = new Image();
				this.customerLogo.src = `/1.0/Customer/Logo/${ res.IDCustomer }`;
				this.logosInitialized = true;
			}
		}).catch((err) =>
		{
			console.log('[QRCodeProvider] Error fetching customer logo, leaving logo field empty.');
		}).finally(() =>
		{
			delete this._logosPromise;
		});
		return this._logosPromise;
	}

	/**
	 * The current session, from the SessionProvider's copy when it has one (no extra CheckSession round trip).
	 * @return {Promise<any>}
	 */
	async _resolveSession()
	{
		const tmpSessionProvider = this.pict.providers?.SessionProvider;
		if (tmpSessionProvider && (typeof tmpSessionProvider.getSession === 'function'))
		{
			const tmpSession = await tmpSessionProvider.getSession();
			if (tmpSession)
			{
				return tmpSession;
			}
		}
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON('CheckSession', (pError, pResult) => (pError ? reject(pError) : resolve(pResult)));
		});
	}

	async getCustomerLogo()
	{
		const tmpSession = await this._resolveSession();
		const customerID = tmpSession?.CustomerID || tmpSession?.IDCustomer;
		return new Promise((resolve, reject) =>
		{
			let tmpRequestOptions = (
				{
					url: this.pict.HeadlightRestClient.formatUrl(`Customer/Logo/${ customerID }`),
				});
			tmpRequestOptions = this.pict.HeadlightRestClient._prepareRequestOptions(tmpRequestOptions);
			this.pict.HeadlightRestClient.restClient.getRawText(tmpRequestOptions, (pError, pResponse, pBody) =>
			{
				pError = this.pict.HeadlightRestClient._checkForError(pError, pResponse, pBody);
				if (pError)
				{
					reject(pError);
				}
				resolve(pResponse.statusCode === 200 ? { IDCustomer: customerID, Body: pBody } : null);
			});
		});
	}

	generateNewQRCode(sample)
	{
		let QRCode = '';
		const user = this.pict.AppData?.FullUserRecord;
		// if we are using legacy format, we also need to get the user record to use for the QR code hash generation
		if (this.pict.AppData?.CurrentUser?.CustomerID == 10)
		{
			this.pict.log.info('Using Legacy Sample GUID format.');
			// LADOTD QRs feed SiteManager, which keys on the user's external (SiteManager) id. With no
			// ExternalSyncGUID, mint a deterministic placeholder "HL<IDUser>" — matching the materials app
			// (sample.ts generateGUID) — so the format stays valid instead of degrading to a plain user GUID.
			QRCode = this.pict.fable.services.HeadlightQRCodeHash.generateQRCodeHash({
				GUIDPrefix: user?.ExternalSyncGUID || `HL${this.pict.AppData?.CurrentUser?.UserID}`,
				LegacyFormat: true,
			});
		}
		else
		{
			this.pict.log.info('Using Standard Sample GUID format.');
			QRCode = this.pict.fable.services.HeadlightQRCodeHash.generateQRCodeHash({ IDProject: sample.IDProject, IDUser: this.pict.AppData?.CurrentUser?.UserID });
		}
		return QRCode;
	}

	/**
	 * Handler to pass to the QRBus instance, this function gets an entity by its QRCode.
	 * @param {String} entity
	 * @param {String} qr
	 * @return {Promise<Object|Error>}
	 */
	getByQRCode = async (entity, qr) =>
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`${ entity }s/By/QRCode/${ qr }`, (error, result) =>
			{
				if (error)
				{
					reject(error);
				}
				else
				{
					if (entity === 'Location' && result?.[0]?.ParentIDLocation)
					{
						this.pict.HeadlightRestClient.getJSON(`${ entity }s/By/IDLocation/${ result[0].ParentIDLocation }`, (error, parentResult) =>
						{
							if (error)
							{
								reject(error);
							}
							else
							{
								if (parentResult.length)
								{
									result[0].ParentLocation = parentResult[0];
								}
								resolve(result[0]);
							}
						});
					}
					else if (result.length)
					{
						resolve(result[0]);
					}
					else
					{
						reject(new Error('No Item found with that QR Code.'));
					}
				}
			});
		});
	};

	/**
	 * Generic "get entity by ID" function.
	 * @param {String} entity - the type of entity
	 * @param {Number} ID -  the id of the entity
	 */
	getByID = async (entity, ID) =>
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`${ entity }/${ ID }`, (error, result) =>
			{
				if (error)
				{
					reject(error);
				}
				else
				{
					resolve(result);
				}
			});
		});
	};

	/**
	 * Registers the "application level" handler for scanning events (when not in a workflow).
	 * @param {Function} func
	 */
	registerGlobalHandler(func)
	{
		this.globalHandler = func;
		this.handler = func;
	}

	/**
	 * Registers a workflow handler for scanning events (that temporarily replaces the global handler).
	 * Also resets the "last camera event".
	 * @param {Function} func
	 */
	startWorkflow(func)
	{
		this.handler = func;
		this.cameraKey = `${ Date.now() }`;
		this.lastCameraEvent =
		{
			time: Date.now(),
			content: '',
		};
	}

	/**
	 * Deregisters any current workflow handler for scanning events (and sets handling back to the global handler).
	 * Also resets the "last camera event".
	 */
	stopWorkflow()
	{
		this.handler = this.globalHandler;
		this.cameraKey = '';
		this.lastCameraEvent =
		{
			time: Date.now(),
			content: '',
		};
	}

	/**
	 * Passes events from the QRBus library on through to the current handler.
	 * @param {QREvent} event
	 */
	passEvent = (event) =>
	{
		this.handler(event);
	};

	/**
	 * Listener that watches for scan events from a barcode scanner and then sends them to the QRBus instance.
	 * @param {KeyboardEvent} event
	 */
	scannerWatcher = (event) =>
	{
		// Don't look for scan events on the login page.
		const isLoginPageActive = document.querySelector('#HeadlightLogin-Standard-Wrap.active:not(.is-hidden)');
		if (isLoginPageActive)
		{
			return;
		}
		// Leaving a bunch of comments in here with code that can be uncommented to test timing issues with scan events.
		//window.calledStart = window.called ? window.calledStart : performance.now();
		//window.called = true;
		//let k = Math.random();
		//window.logger.push(k + ' listener started ' + Date.now() + ' / ' + performance.now() + ' ' + event.key);
		const now = Date.now();
		//let nowPerf = performance.now();
		let sinceLast = now - this.scannerTimer;
		//let sinceLastPerf = nowPerf - this.perfTimer;
		if (sinceLast > 200)
		{
			//window.logger.push('Hit gap ' + sinceLast + ' ' + now + ' ' + this.scannerTimer + ' / ' + sinceLastPerf + ' ' + nowPerf + ' ' + this.perfTimer);
			this.scannerTimer = Date.now();
			//this.perfTimer = performance.now();
			this.capturedQRCode = '';
		}
		else if (event.key === 'Enter')
		{
			//window.called = false;
			//console.log(window.logger);
			//console.log(window.elapsed);
			if (this.capturedQRCode)
			{
				console.log('captured scan: ', this.capturedQRCode);
			}
			if (this.capturedQRCode.length > 10)
			{
				this.QRBus.receiveScan(this.capturedQRCode);
			}
			else
			{
				console.log('Cancelling scan b/c qr code is too small: ', this.capturedQRCode);
			}
			this.capturedQRCode = '';
		}
		sinceLast = now - this.scannerTimer;
		//sinceLastPerf = nowPerf - this.perfTimer;
		if (sinceLast <= 200)
		{
			this.capturedQRCode += event.key;
			this.scannerTimer = Date.now();
			//this.perfTimer = performance.now();
		}
		//window.logger.push(k + ' listener ended ' + this.capturedQRCode + ' ' + this.scannerTimer + ' ' + Date.now() + ' ' + sinceLast + ' / ' + this.perfTimer + ' ' + performance.now() + ' ' + sinceLastPerf);
		//window.elapsed = performance.now() - window.calledStart;
	};

	/**
	* @typedef {object} QRCodeOptions
	* @property {string} [previewID] - The ID of a DOM element to render the preview into.
	* @property {boolean} [raw] - if true, code is used as is and not put in a URL
	* @property {string} [type] - If passed, the passed type will be included in the qr code url
	* @property {string} [printStyle] - the style of label to print, current options are 'thin' and 'wide'
	* @property {number} [size] - same as width and height
	* @property {string[]} [footers] - additional text to print below the QR code
	* @property {string[]} [headers] - additional text to print above the QR code
	* @property {string[]} [bigRows] - lists footer rows that should be displayed in "bigger" text
	*/

	/**
	 * Passthrough to the "scanQRCode" method.
	 * @param {String} id
	 */
	async scan(id)
	{
		return await this.scanQrCode(id);
	}

	/**
	 * Opens a QR scanner camera instance onto an element with a given id.
	 * @param {String} id
	 * @param {Object} [options] - options for the scanner
	 */
	async openScanner(id, options = {})
	{
		return await this.scanQrCode(id, true, options);
	}

	/**
	 * Closes any open QR Scanner camera instance.
	 */
	async closeScanner()
	{
		return await new Promise((resolve, reject) =>
		{
			async function stopper(tries)
			{
				// @ts-ignore
				const localThis = _Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode;
				if (!localThis.canvasElement)
				{
					if (!tries || tries < 10)
					{
						console.log('deferring scanner stop: ', tries);
						setTimeout(()=>
						{
							stopper(tries ? tries + 1 : 1);
						}, 200);
					}
					else
					{
						console.log('Failed to stop camera, exhausted retries.');
						resolve();
					}
					return;
				}
				// @ts-ignore
				if (_Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode && _Pict.providers.QRCodeProvider._qrCodeScanner?.html5Qrcode?.stateManagerProxy?.isScanning())
				{
					// @ts-ignore
					await _Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode.stop();
				}
				resolve();
			};
			stopper();
		});
	}

	/**
	 * @param {string} code
	 * @param {QRCodeOptions} [options]
	 * @return {string} - base64 encoded image
	 */
	create(code, options = {})
	{
		const text = options.raw ? code : `${this.pict.providers.UtilityProvider.buildLinksUrl()}${ options.type ? `/${ options.type }` : '' }/qr/${code}`;
		return this.generateQRCodeImage({
			text: text,
			width: options.size || 256,
			height: options.size || 256,
		});
	}

	/**
	 * Formats strings for the "wide" print layout.
	 * @param {Array<String>} displayStrings
	 * @return {Array<String>}
	 */
	formatStringsForPrinting(displayStrings)
	{
		const finaldisplayStrings = [];
		displayStrings.forEach((s) =>
		{
			const tokens = s.split('COMPUTED_COLUMN');
			if (!tokens?.[1])
			{
				finaldisplayStrings.push(s.length > 80 ? s.substring(0, 77) + '...' : s);
				return;
			}
			if (tokens[0].length > 35)
			{
				tokens[0] = tokens[0].substring(0, 35) + '...';
			}
			if (tokens[1]?.length > 35)
			{
				tokens[1] = tokens[1].substring(0, 35) + '...';
			}
			finaldisplayStrings.push(`${ tokens[0].padEnd(40) }${ tokens[1] }`);
		});
		return finaldisplayStrings;
	}

	/**
	 * Using a Sample record, constructs the set of information to use while printing. Used for the "wide" template.
	 * @param {Object} record
	 * @return {Promise<Array<String>>}
	 */
	async getSamplePrintablesWide(record)
	{
		const formatLocation = (city, state, country) =>
		{
			if (!city && !state && !country)
			{
				return 'N/A';
			}
			else if (city && state)
			{
				return `${ city }, ${ state }`;
			}
			else if ((city && country) || (state && country))
			{
				return `${ city || '---' }, ${ state || '---' }, ${ country || '---' }`;
			}
			else
			{
				return city || state || country;
			}
		};
		const center = 'COMPUTED_COLUMN';
		const sampledByUser = record.SampledByIDUser ? (await this.getByID('User', record.SampledByIDUser)) : (await this.getByID('User', record.CreatingIDUser));
		const displayStrings = [
			`Description: ${ record.Name || 'N/A' }`,
			`Received: ${ record.ReceivedDate ? this.pict.fable.Dates.dayJS(record.ReceivedDate).format('MM/DD/YYYY') : 'N/A' }${ center }Sampled: ${ record.DesignatedTime ? this.pict.fable.Dates.dayJS(record.DesignatedTime).format('MM/DD/YYYY') : 'N/A' }`,
			`Container: ${ record.Container || 'N/A' }${ center }Container Count: ${ record.ContainerCount || 'N/A' }`,
			`Source: ${ record.ReceivedFrom || 'N/A' }${ center }Source Loc.: ${ formatLocation(record.SourceCity, record.SourceState, record.SourceCountry) }`,
		];
		const currentSampleLocation = parseInt(record?.Extension?.StorageLocation) ? (await this.pict.providers.LocationProvider.getLocationByID(parseInt(record?.Extension?.StorageLocation))) : null;
		displayStrings.push(`Storage Loc.: ${ currentSampleLocation?.Name || 'N/A' }${ center }Logged By: ${ sampledByUser.NameFirst } ${ sampledByUser.NameLast }`);
		// Probably applicable to most customers but commenting out for now for paragon.
		/*displayStrings.push(`Material: ${ record.IDMaterial ? (await this.getByID('Material', record.IDMaterial)).Name : 'N/A' }${ center }Location: ${ currentSampleLocations.map((loc)=>{ return `${ loc.Name }${ loc.ParentLocation ? ` linked to: ${ loc.ParentLocation.Name }` : '' }`; }).join(', ') || 'N/A' }`);
		if (record.IDOrganization)
		{
			const org = await this.getByID('Organization', record.IDOrganization);
			displayStrings.push(`Producer: ${ org.Name || 'N/A' }${ center }Producer Location: ${ org.City && org.State ? `${ org.City }, ${ org.State }` : 'N/A' }`);
		}*/
		// Probably applicable to most customers but commenting out for now for paragon.
		/*
		let plantString = '';
		if (record.IDProductionPlant)
		{
			const plant = await this.getByID('ProductionPlant', record.IDProductionPlant);
			plantString += `Plant: ${ plant.Name || 'N/A' }`;
		}
		if (record.IDProject)
		{
			const project = await this.getByID('Project', record.IDProject);
			plantString += plantString ? `${ center }Project: ${ project.Name || 'N/A' }` : `Project: ${ project.Name || 'N/A' }`;
		}
		if (plantString)
		{
			displayStrings.push(plantString);
		}*/
		displayStrings.push(`Notes: ${ record.Notes || 'N/A' }`);
		return this.formatStringsForPrinting(displayStrings);
	}

	/**
	 * Using a Location record, constructs the set of information to use while printing. Used for the "wide" template.
	 * @param {Object} record
	 * @return {Array<String>}
	 */
	getLocationPrintablesWide(record)
	{
		//const center = 'COMPUTED_COLUMN';
		const displayStrings =
		[
			`Name: ${ record.Name || 'N/A' }`,
		];
		return this.formatStringsForPrinting(displayStrings);
	}

	/**
	 * Using a Sample record, constructs the set of information to use while printing.
	 * @param {Object} record
	 * @param {String} [printStyleType]
	 * @return {Promise<Array<String>>}
	 */
	async getSamplePrintables(record, printStyleType)
	{
		const printStyle = printStyleType || this.printStyle;
		if (printStyle === 'wide')
		{
			return await this.getSamplePrintablesWide(record);
		}
		const sampledByUser = record.SampledByIDUser ? (await this.getByID('User', record.SampledByIDUser)) : (await this.getByID('User', record.CreatingIDUser));
		const displayStrings = [
			record.QRCode,
			`Name: ${ record.Name || 'N/A' }`,
			`Sample Date: ${ this.pict.fable.Dates.dayJS(record.DesignatedTime || record.CreateDate).toISOString() }`,
			`Material: ${ record.IDMaterial ? (await this.getByID('Material', record.IDMaterial)).Name : 'N/A' }`,
			`Sampled By: ${ sampledByUser.NameFirst } ${ sampledByUser.NameLast }`,
		];
		const currentSampleLocations = await this.pict.providers.LocationProvider.getLocationsBySampleID(record.IDSample);
		displayStrings.push(`Location: ${ currentSampleLocations.map((loc)=>
		{
			return `${ loc.Name }${ loc.ParentLocation ? ` linked to: ${ loc.ParentLocation.Name }` : '' }`;
		}).join(', ') || 'N/A' }`);
		if (record.IDProject)
		{
			const project = await this.getByID('Project', record.IDProject);
			displayStrings.push(`Project: ${ project.Name || 'N/A' }`);
		}
		if (record.IDOrganization)
		{
			const org = await this.getByID('Organization', record.IDOrganization);
			displayStrings.push(`Producer: ${ org.Name || 'N/A' }`);
			displayStrings.push(`Producer Location: ${ org.City && org.State ? `${ org.City }, ${ org.State }` : 'N/A' }`);
		}
		if (record.IDProductionPlant)
		{
			const plant = await this.getByID('ProductionPlant', record.IDProductionPlant);
			displayStrings.push(`Plant: ${ plant.Name || 'N/A' }`);
		}
		displayStrings.push(`Notes: ${ record.Notes || 'N/A' }`);
		return displayStrings;
	}

	/**
	 * Using a Location record, constructs the set of information to use while printing.
	 * @param {Object} record
	 * @param {String} [printStyleType]
	 * @return {Array<String>}
	 */
	getLocationPrintables(record, printStyleType)
	{
		const printStyle = printStyleType || this.printStyle;
		if (printStyle === 'wide')
		{
			return this.getLocationPrintablesWide(record);
		}
		const headlightLogo = 'HLLOGO';
		const color = (color) =>
		{
			return `COLORBEGIN${ color }COLOREND`;
		};
		const displayStrings =
		[
			`${ headlightLogo } ${ color('darkgrey') }Location`,
		];
		return displayStrings;
	}

	/**
	 * Marks the set of rows to apply a forced larger font to for Samples.
	 * @return {Array<String>}
	 */
	getSampleBigRows()
	{
		return [
			'Description',
		];
	}

	/**
	 * Make sure the per-customer label configuration is loaded into `typeConfig`, once.
	 *
	 * `typeConfig` drives `getTemplatedEntitySettings` — PrintStyle (thin/wide), the title template, and
	 * which fields print in which row/column. Apps that load it at startup (LIMS) leave nothing to do here;
	 * apps that never did (the panels/document context) would otherwise print the built-in fallback layout
	 * and silently ignore the customer's printer configuration.
	 *
	 * Deliberately NOT called from `printDecoratedEntity`: that would also change label output for
	 * consumers which have never loaded printable manifests and do not expect the LIMS layout (e.g.
	 * bridge_inspection_v2 printing through ImprovedTabularRecordSetProvider). Callers opt in.
	 *
	 * Never throws — a failed load leaves the built-in layout in place.
	 * @return {Promise<void>}
	 * @async
	 */
	async ensurePrintableManifests()
	{
		// an app that loaded them at startup has already populated typeConfig; nothing to do
		if (this._PrintableManifestsEnsured || Object.keys(this.typeConfig || {}).length > 0)
		{
			this._PrintableManifestsEnsured = true;
			return;
		}
		this._PrintableManifestsEnsured = true;
		try
		{
			if (!this.pict.ManifestPackagingService)
			{
				this.pict.addAndInstantiateServiceType('ManifestPackagingService', libManifestPackagingService);
			}
			await this.pict.ManifestPackagingService.loadPrintableManifests(_PrintableManifestEntities, {}, _PrintableManifestProduct);
			this.pict.log.info('Loaded the printable manifests for QR labels.');
		}
		catch (pError)
		{
			this.pict.log.warn(`Could not load the printable manifests; QR labels will use the default layout: ${pError?.message || pError}`);
		}
	}

	applyTypeConfig(tc)
	{
        this.typeConfig = Object.assign(this.typeConfig, tc);
	}

	addManifestForType(type, manifest)
	{
        this.typeConfig[type] = manifest;
	}

	_getHumanReadableEntityName(pEntity)
	{
		return pEntity.replace(/([a-z])([A-Z])/g, '$1 $2'); // Add space before capital letters
	}

	async getTemplatedEntitySettings(entity, record)
	{
		if (!this.pict.services.HeadlightQuickSummaries || !this.typeConfig[entity])
		{
			return null;
		}
		const returnObject =
		{
			headers: [],
			footers: [],
			bigRows: [],
			printStyle: this.typeConfig[entity].PrintStyle || 'thin',
		};
		const processedRecord = await this.pict.services.HeadlightQuickSummaries.processRecord(entity, record, 'Printable', this.typeConfig);
		returnObject.headers = [ this.typeConfig[entity].TitleTemplate ? this.fable.parseTemplate(this.typeConfig[entity].TitleTemplate, processedRecord) : this._getHumanReadableEntityName(entity) ];
		const fields = Object.keys(this.typeConfig[entity].Descriptors);
		const rows = [];
		let minRows = 0;
		for (const f of fields)
		{
			const field = this.typeConfig[entity].Descriptors[f];
			if (field.Row && field.Row > minRows)
			{
				minRows = field.Row;
			}
		}
		for (let x = 0; x < minRows; x++)
		{
			rows.push([]);
		}
		for (const f of fields)
		{
			const field = this.typeConfig[entity].Descriptors[f];
			if (returnObject.printStyle == 'thin' && field.Column > 1)
			{
				console.warn(`Field ${ f } was not printed b/c the print style is set to "thin" and the Column # was greater than 1.`);
				continue;
			}
			if (returnObject.printStyle == 'wide' && field.Column > 2)
			{
				console.warn(`Field ${ f } was not printed b/c the print style is set to "wide" and the Column # was greater than 2.`);
				continue;
			}
			if (!field.Row)
			{
				console.warn(`Field ${ f } was not printed b/c a Row was not specified`);
				continue;
			}
			rows[field.Row - 1].push(Object.assign(field, { FieldAddress: f }));
			rows[field.Row - 1] = rows[field.Row - 1].sort((a, b) =>
			{
				if (a.Column > b.Column)
				{
					return 1;
				}
				else if (b.Column > a.Column)
				{
					return -1;
				}
				return 0;
			});
		}
		const generatePrintableLine = (row) =>
		{
			if (!row.length)
			{
				return null;
			}
			let returnString = `${ row[0].Name }: ${ row[0].HeadlightPrintable?.ValueTemplate ? this.fable.parseTemplate(row[0].HeadlightPrintable?.ValueTemplate, processedRecord) : (processedRecord[row[0].FieldAddress] || 'N/A') }`;
			if (returnObject.printStyle == 'wide' && row[1])
			{
				returnString += `COMPUTED_COLUMN${ row[1].Name }: ${ row[1].HeadlightPrintable?.ValueTemplate ? this.fable.parseTemplate(row[1].HeadlightPrintable?.ValueTemplate, processedRecord) : (processedRecord[row[1].FieldAddress] || 'N/A') }`;
			}
			return returnString;
		};
		const tempFooters = [];
		for (const r of rows)
		{
			const line = generatePrintableLine(r);
			if (line)
			{
				tempFooters.push(generatePrintableLine(r));
			}
		}
		returnObject.footers = this.formatStringsForPrinting(tempFooters);
		return returnObject;
	}

	/**
	 * Prints a QR Code for an entity but decorated with additional information about the entity.
	 * @param {String} entity - The entity type being printed.
	 * @param {String} qr - The QR Code of that entity.
	 * @param {String} [previewID] - Tells the function this is a print preview and shouldn't open a print modal.
	 * 								 This is the DOM id of the container to place the preview in.
	 * @param {String} [printStyleOverride] - Allows overriding of the default printing style.
	 * @return {Promise}
	 */
	async printDecoratedEntity(entity = 'Sample', qr, previewID, printStyleOverride)
	{
		const printStyle = printStyleOverride ? printStyleOverride : entity === 'Sample' ? 'wide' : this.printStyle;
		await this.initializeLogos();
		try
		{
			const record = await this.getByQRCode(entity, qr);
			if (this.pict.providers.RecordExtension)
			{
				await this.pict.providers.RecordExtension.extendRecords(entity, [ record ]);
				if (record.Extended)
				{
					for (const k of Object.keys(record.Extended))
					{
						record[k] = record.Extended[k];
					}
				}
			}
			let settingsObj =
			{
				previewID: previewID,
				raw: entity !== 'Sample',
				printStyle: printStyle,
				headers: entity !== 'Sample' ? record?.Name ? [ record.Name ] : [ entity ] : [],
				bigRows: this[`get${ entity }BigRows`] ? this[`get${ entity }BigRows`]() : [],
				footers: this[`get${ entity }Printables`] ? await this[`get${ entity }Printables`](record, printStyle) : [ qr ],
			};
			if (entity === 'Sample')
			{
				settingsObj.headers.push('PSID ' + (record.LabControlNumber || '---'));
			}
			const templatedEntitySettings = await this.getTemplatedEntitySettings(entity, record);
			// If there is a manifest, allow that to override the printing settings.
			if (templatedEntitySettings)
			{
				settingsObj = Object.assign(settingsObj, templatedEntitySettings);
			}
			const code = entity === 'Sample' ? qr : `${ this.pict.providers.UtilityProvider.buildLinksUrl() }/${ entity }/qr/${ qr }`;
			return await this.print(code, settingsObj);
		}
		catch (e)
		{
			console.log(`Error printing ${ entity } with QR code ${ qr }: `, e);
			return null;
		}
	}

	/**
	 * @param {string} code
	 * @param {QRCodeOptions} [options]
	 *
	 * @return {Promise<'canceled' | 'printed' | undefined>}
	 */
	print(code, options = {})
	{
		const printStyle = options.printStyle || this.printStyle;
		const text = options.raw ? code : `${this.pict.providers.UtilityProvider.buildLinksUrl()}/qr/${code}`;
		const height = 100;
		const topPadding = printStyle === 'wide' ? 100 : 50;
		const footers = [];
		const headers = [];
		let overallSize = 128;
		if (options.footers?.length)
		{
			for (const subText of options.footers)
			{
				footers.push({
					text: subText,
					padding: [ topPadding, 64, 0, 64 ],
					height: height,
				});
				overallSize += height + topPadding;
			}
		}
		if (options.headers?.length)
		{
			for (const subText of options.headers)
			{
				headers.push({
					text: subText,
					padding: [ 0, 64, topPadding, 64 ],
					height: height,
				});
				overallSize += height + topPadding;
			}
		}
		// This is the max percentage of the sticker the text should take up. QR code will take the remaining space.
		const scaleFactor = printStyle === 'wide' ? 65 : 60;

		// Calculate the size the sticker image needs to be based on the text size and scale factor.
		const adjustedScale = (overallSize / scaleFactor) * 100;

		return this.printQrCode({
			previewID: options.previewID,
			text: text,
			width: printStyle === 'wide' ? adjustedScale * (3/2) : adjustedScale,
			height: adjustedScale,
			padding: 64,
			footer: footers,
			header: headers,
			bigRows: options.bigRows || [],
			printStyle: printStyle,
		});
	}

	/* -- Print QR Code Methods ---------------------------------------- */

	/**
	* Converts base64 data to an object url
	* @param {string} b64Data
	* @return {string}
	* @private
	*/
	_toObjectUrl(b64Data)
	{
		if (!b64Data.startsWith('data:'))
		{
			throw new Error('Invalid base64 data, expected data:{contentType};base64,{data}');
		}
		const contentType = b64Data.substring('data:'.length, b64Data.indexOf(';'));
		const content = b64Data.substring(b64Data.indexOf(';base64,') + 8);
		const blob = this._b64toBlob(content, contentType);
		return URL.createObjectURL(blob);
	}

	/**
	* Converts a base64 string to a blob
	* @param {string} b64Data
	* @param {string} contentType
	* @param {number} sliceSize
	* @return {module:buffer.Blob}
	* @private
	*/
	_b64toBlob(b64Data, contentType='', sliceSize=512)
	{
		const byteCharacters = atob(b64Data);
		const byteArrays = [];

		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize)
		{
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++)
			{
				byteNumbers[i] = slice.charCodeAt(i);
			}

			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}

		return new Blob(byteArrays, { type: contentType });
	}

	/**
	* YOU PROBABLY DONT WANT THIS METHOD, USE `print` INSTEAD
	*
	* Prints using the newTab method, this is one of several ways to print a QR code.
	* See the `print` method for more details.
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	printInNewTab(options)
	{
		const src = this.generateQRCodeImage(options);
		const blobUrl = this._toObjectUrl(src);
		window.open('./Global/qrcode/printTab.html?v=' + blobUrl, '_blank');

		return undefined;
	}

	/**
	* "Prints" a preview of the printout into a DOM element.
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	printPreview(options)
	{
		const src = this.generateQRCodeImage(options);
		const blobUrl = this._toObjectUrl(src);
		const el = document.querySelector(`#${ options.previewID }`);
		if (el)
		{
			el.innerHTML = /*html*/`<img src="${ blobUrl }" />`;
		}

		return undefined;
	}

	/**
	* YOU PROBABLY DONT WANT THIS METHOD, USE `print` INSTEAD
	*
	* Prints using an embedded iframe, this is one of several ways to print a QR code.
	* See the `print` method for more details.
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	printInline(options)
	{
		const src = this.generateQRCodeImage(options);

		const blobUrl = this._toObjectUrl(src);

		const { printFrame } = this.getGenerationLayers();

		let resolveDeferred;
		const deferred = new Promise(function(resolve, reject)
		{
			resolveDeferred = resolve;
		});

		printFrame.contentWindow.document.open();
		window.onfocus=function()
		{
			resolveDeferred();
		};
		window.addEventListener('focus', function(event)
		{
			resolveDeferred();
		}, { once: true });

		printFrame.contentWindow.document.write(`<html lang="en">
			<head>
				<title>QR Code</title>
				<style>
					html, body {
						overflow-x: hidden;
					}
					
					img {
						height: 100%;
						width: 100%;
						max-height: 600px; /* cant seem to get this to scale right, i give up */
						object-fit: contain;
						flex: 1;
					}
					
					.column {
						display: flex;
						flex-direction: column;
						flex: 1;
					}
					
					button {
						appearance: none;
						display: block;
						width: calc(100% - 32px);
						box-sizing: border-box;
						padding: 12px 16px;
						font-size: 36px;
						background-color: #38b2ff;
						border: 1px solid #008ee3;
						color: white;
						font-weight: bold;
						margin: 16px;
						max-width: 600px;
						align-self: center;
					}
					
					@media print {
						@page {
							size: 62mm 100mm;
							margin: 0cm !important;
							padding: 0cm !important;
						}
					
						html, body {
							padding: 0 !important;
							margin: 0 !important;
							position: relative !important;
							inset: 0 !important;
						}
					
						/*@media print {*/
						/*}*/
						* { margin: 0 !important; padding: 0 !important; }
						#controls, .footer, .footerarea{ display: none; }
						html, body {
							/*changing width to 100% causes huge overflow and wrap*/
							height:100%;
							overflow: hidden;
							background: #FFF;
							font-size: 9.5pt;
							page-break-inside: avoid;
						}
						.template { width: auto; left:0; top:0; }
						li { margin: 0 0 10px 20px !important;}
					
					
						button {
							display: none;
						}
					
						img {
							position: absolute;
							top: 0;
							left: 0;
							width: 100%;
							align-self: initial;
					
							object-fit: contain;
							flex: 0;
						}
					}
				</style>
			</head>
			<body onload="print()">
				<img src="${blobUrl}" alt="${options.text}"/>
			</body>
			</html>`);
		printFrame.contentWindow.document.close();

		return deferred;
	}

	async printNative(options)
	{
		const src = this.generateQRCodeImage(options);
		return this.fable.NativeBridge.printImage({
			jobName: 'QR Code',
			base64Image: src,
		});
	}

	/**
	* Prints a QR code using the best method available for the current browser.
	*
	* @param {QRCodePrinterOptions} options
	* @return {Promise<'canceled' | 'printed' | undefined>}
	*/
	async printQrCode(options)
	{
		await this.initializeLogos();
		if (options.previewID)
		{
			return this.printPreview(options);
		}
		const method = this.getPrintMethod();
		if (method === 'native')
		{
			return this.printNative(options);
		}
		else if (method === 'window')
		{
			return this.printInNewTab(options);
		}
		else
		{
			return this.printInline(options);
		}
	}

	/**
	* Gets the ideal printing method for the given browser.
	*
	* @return {'native'|'inline'|'window'}
	*/
	getPrintMethod()
	{
		const ua = navigator.userAgent;
		const iOS = ua.match(/iPad/i) || ua.match(/iPhone/i);
		const webkit = ua.match(/WebKit/i);
		const isSafariMobile = iOS && webkit && !ua.match(/CriOS/i);

		if (this.fable.NativeBridge.hasNativeSupport())
		{
			return 'native';
		}
		else if (isSafariMobile)
		{
			return 'window';
		}
		else
		{
			return 'inline';
		}
	}

	/**
	 * This is a copy of a method inside the HTML5QRCode lib. It should be exactly the same except it has a fix to handle a bug where it can sometimes call removeChild on an element that doesn't exist.
	 * TODO: Hopefully the library fixes this bug themselves at some point, there is an open issue on their github for it.
	 * @param {Number} [tries] - how many times the scanner has tried to run. WE will give up after a few retries.
	 * @return {Promise<void>}
	 */
	localScannerStop(tries)
	{
		// @ts-ignore
		const localThis = _Pict.providers.QRCodeProvider._qrCodeScanner.html5Qrcode;
		if (!localThis.stateManagerProxy.isScanning())
		{
			console.log('Returning early, scanner is not already running.');
			return;
		}

		const toStoppedStateTransaction = localThis.stateManagerProxy.startTransition(1);

		this.shouldScan = false;
		if (localThis.foreverScanTimeout)
		{
			clearTimeout(localThis.foreverScanTimeout);
		}

		// Removes the shaded region if exists.
		const removeQrRegion = () =>
		{
			if (!localThis.element)
			{
				return;
			}
			const childElement = document.getElementById('qr-shaded-region');
			if (childElement)
			{
				localThis.element.removeChild(childElement);
			}
		};

		return localThis.renderedCamera.close().then(() =>
		{
			localThis.renderedCamera = null;

			if (localThis.element && localThis.canavsElement)
			{
				localThis.element.removeChild(localThis.canvasElement);
				localThis.canvasElement = null;
			}

			removeQrRegion();
			if (localThis.qrRegion)
			{
				localThis.qrRegion = null;
			}
			if (localThis.context)
			{
				localThis.context = null;
			}

			toStoppedStateTransaction.execute();
			try
			{
				localThis.hidePausedState();
			}
			catch (e)
			{
				console.log('Scanner ui element not present, skipping.');
			}
			localThis.isScanning = false;
			return Promise.resolve();
		});
	}

	/* -- QR Code Scan Methods ---------------------------------------- */

	/**
	* Scans a QRCode and gives you back the result
	* @param {String} [id] - optional, an id of an element to inject the scanner onto.
	* @param {Boolean} [keepOpen] - optional, keeps the camera in an open scanning mode.
	* @param {Object} [pOptions] - optional, options for the scanner
	* @return {Promise<String>}
	*/
	async scanQrCode(id, keepOpen, pOptions = {})
	{
		const Html5QrcodeScanner = window.Html5QrcodeScanner;
		if (typeof Html5QrcodeScanner === 'undefined')
		{
			throw new Error('QRCode scanning is not supported. Missing library Html5QrcodeScanner');
		}

		if (!this._qrCodeScanner)
		{
			if (!id)
			{
				this._dialog = new Dialog({
					closeOnBackdropClick: true,
					body: `<div id="qrcode-scanner" style="width: 100%; height: 100%;"></div>`,
				});
			}
			this._qrCodeScanner = new window.Html5QrcodeScanner(id || 'qrcode-scanner', {
				fps: 20,
				rememberLastUsedCamera: true,
				videoConstraints: { facingMode: 'environment' },
				qrbox: {
					width: pOptions?.isPhoneDevice ? 150 : 250,
					height: pOptions?.isPhoneDevice ? 150 : 250,
				},
				// turn off file upload feature, it doesnt work well with iOS devices (safari)
				// enum found at https://github.com/mebjas/html5-qrcode/blob/master/src/core.ts#L76
				supportedScanTypes: [ 0 ],
			});
		}
		// Make sure events can't "persist" between instances of opening the cart (i.e. if an event fires before the cart closes, it should not get picked up if the cart closes and reopens).
		const instanceCameraKey = `${ this.cameraKey }`;
		if (keepOpen)
		{
			this._qrCodeScanner.render((result)=>
			{
				if (this.cameraKey && instanceCameraKey === this.cameraKey && Date.now() - this.lastCameraEvent.time > 1000 && this.lastCameraEvent.content !== result)
				{
					this.lastCameraEvent =
					{
						time: Date.now(),
						content: result,
					};
					this.QRBus.receiveScan(result);
				}
			});
			const embedUI = document.querySelector('#QRPanel-ScannerBox__dashboard_section');
			// Remove this for now until we have a better solution for detecting mobile.
			/*const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
			if (!isMobile)
			{
				embedUI.classList.add('is-hidden');
			}*/
			if (embedUI.innerHTML.includes('Request Camera Permissions'))
			{
				// @ts-ignore
				document.querySelector('#html5-qrcode-button-camera-permission').click();
			}
			// Replace the stop method with a local version that fixes a bug in it.
			// See method jsdoc comment for more details.
			this._qrCodeScanner.html5Qrcode.stop = this.localScannerStop;
			return Promise.resolve('');
		}
		else
		{
			return await new Promise((resolve, reject) =>
			{
				if (this._dialog)
				{
					this._dialog.show();
				}
				this._qrCodeScanner.render((result) =>
				{
					if (this._dialog)
					{
						try
						{
							this._qrCodeScanner.html5Qrcode.stop();
						}
						catch (e)
						{
							console.error('Error stopping scanner', e);
						}
						this._dialog.hide();
					}
					resolve(qrTranslator.translateQRCode(result).code);
				});
			});
		}
	}

	/* -- QR Code Generation Methods ---------------------------------------- */

	/**
	 * @typedef {Object} Footer
	 * @property {string} footer.text - The text to put in the footer
	 * @property {number} footer.height - The height of the footer, this will be used to calculate the font size
	 * @property {number|Array<number>} [footer.padding] - The padding around the footer
	 */

	/**
	 * @typedef {Object} Header
	 * @property {string} header.text - The text to put in the header
	 * @property {number} header.height - The height of the header, this will be used to calculate the font size
	 * @property {number|Array<number>} [header.padding] - The padding around the header
	 */

	/**
	* @typedef {Object} QRCodePrinterOptions
	* @property {string} text - The text to encode in the QR code
	* @property {string} [previewID] - The ID of a DOM element to render the print preview into.
	* @property {number} [width] - The width of the QR code
	* @property {number} [height] - The height of the QR code
	* @property {string} [printStyle] - Style of label to print, current options are 'thin' and 'wide'
	* @property {number|Array<number>} [padding] - The padding around the QR code
	*
	* @property {Array<Header>} [header] - The header to put above the QR code
	*
	* @property {Array<Footer>} [footer] - The footer to put below the QR code

	* @property {Array<String>} [bigRows] - lists footer rows that should be displayed in "bigger" text
	*
	*/

	/**
	* Parse a padding format into a common output of top, left, bottom, right
	* @param {number|Array<number>} padding - The padding to parse
	* @return {{top: number, left: number, bottom: number, right: number}}
	*/
	_parsePadding(padding)
	{
		if (Array.isArray(padding))
		{
			return {
				top: padding[0] ?? 0,
				right: padding[1] ?? 0,
				bottom: padding[2] ?? padding[0] ?? 0,
				left: padding[3] ?? padding[1] ?? 0,
			};
		}
		return {
			top: padding || 0,
			bottom: padding || 0,
			left: padding || 0,
			right: padding || 0,
		};
	}

	/**
	 * @typedef {Object} TextMetrics
	 * @property {Number} usedFontSize
	 */

	/**
	* Given the text and the width and height, this will return the ideal font size for the text to fit within the given width and height
	* @param {object} ctx
	* @param {string} text
	* @param {number} width
	* @param {number} height
	* @return {TextMetrics}
	* @private
	*/
	_setIdealFontSize(ctx, text, width, height)
	{
		let size = 0; const step = .5;
		let tmpSize;
		do
		{
			size += step;
			ctx.font = 'bold ' + size + 'px "Noto Sans Mono", monospace';
			tmpSize = ctx.measureText(text);
		} while (tmpSize.width < width && (tmpSize.actualBoundingBoxAscent + tmpSize.actualBoundingBoxDescent) < height);

		// we can overshoot, check if we did then bring it down a notch
		if (tmpSize.width > width || (tmpSize.actualBoundingBoxAscent + tmpSize.actualBoundingBoxDescent) > height)
		{
			size -= step;
			ctx.font = 'bold ' + size + 'px "Noto Sans Mono", monospace';
			tmpSize = ctx.measureText(text);
		}

		tmpSize.usedFontSize = size;
		return tmpSize;
	}

	/**
	* Generates a QR code image given the provided options.
	* The QRCode library we use only does the QRCode but It's ridiculous to try and
	* get print styles to conform in a standard way. However, what is a great standard is the
	* canvas library! use that to put stuff where we want then make an image from it
	* @param {QRCodePrinterOptions} options
	* @return {string} - base64 encoded image
	*/
	generateQRCodeImage(options)
	{
		if (typeof window.QRCode === 'undefined')
		{
			throw new Error('QRCode generation is not supported. Missing library QRCode');
		}

		// init if we need to

		const { canvas, qrCodeContainer } = this.getGenerationLayers();

		const printStyle = options.printStyle || this.printStyle;

		if (printStyle === 'wide' && !options.previewID)
		{
			canvas.style.width = options.height+'px';
			canvas.style.height = options.width+'px';
			canvas.style.border = '1px solid black';

			canvas.width = options.height;
			canvas.height = options.width;
		}
		else
		{
			canvas.style.width = options.width+'px';
			canvas.style.height = options.height+'px';
			canvas.style.border = '1px solid black';

			canvas.width = options.width;
			canvas.height = options.height;
		}

		const ctx = canvas.getContext('2d');
		if (printStyle === 'wide' && options?.footer?.length && !options.previewID)
		{
			ctx.translate(canvas.width, 0);
			ctx.rotate(Math.PI/2);
		}
		ctx.fillStyle = 'white';
		ctx.fillRect(0, 0, options.width, options.height);

		ctx.save();
		const offset = this._parsePadding(options.padding);
		// given our root padding we want to move everything in by this amount
		// translate the entire thing by that offset
		ctx.translate(offset.left, offset.top);

		let { width, height } = options;
		// adjust the perceived width and height of below by our padding options
		width -= offset.left + offset.right;
		height -= offset.top + offset.bottom;

		const sanitizeText = (string) =>
		{
			const tokens = [ 'HLLOGO', 'COLORBEGIN', 'COLOREND' ];
			for (const t of tokens)
			{
				if (t === 'COLORBEGIN' && string.includes('COLORBEGIN'))
				{
					const fontColor = string.split('COLORBEGIN')[1].split('COLOREND')[0];
					string = string.replace(fontColor, '');
				}
				string = string.replace(t, '');
			}
			return string;
		};

		let ctxFont = 0;
		const textSet = [].concat(options.header || []).concat(options.footer || []);

		const sanitizeTextForTesting = (string) =>
		{
			const double = string.includes('HLLOGO');
			const tokens = [ 'HLLOGO', 'COLORBEGIN', 'COLOREND' ];
			for (const t of tokens)
			{
				if (t === 'COLORBEGIN' && string.includes('COLORBEGIN'))
				{
					const fontColor = string.split('COLORBEGIN')[1].split('COLOREND')[0];
					string = string.replace(fontColor, '');
				}
				string = string.replace(t, '');
			}
			return double && textSet.length > 1 ? string + string : string;
		};

		for (const note of textSet)
		{
			const padding = this._parsePadding(note.padding);
			const tmpInsets = this._setIdealFontSize(
				ctx,
				sanitizeTextForTesting(note.text),
				width - padding.left - padding.right,
				note.height,
			);
			if (tmpInsets.usedFontSize < ctxFont || !ctxFont)
			{
				ctxFont = tmpInsets.usedFontSize;
			}
		}

		let extraBottomOffset = 0;
		if (options?.footer?.length && printStyle === 'wide' && ctxFont)
		{
			ctx.font = 'bold ' + ctxFont + 'px "Noto Sans Mono", monospace';
			if (!options.bigRows)
			{
				options.bigRows = [];
			}
			const tmpSize = ctx.measureText('test');
			const smallFooterSize = (tmpSize.actualBoundingBoxAscent + tmpSize.actualBoundingBoxDescent + options.footer[0].padding[0]);
			ctx.font = 'bold ' + (ctxFont * 1.5) + 'px "Noto Sans Mono", monospace';
			const tmpSizeBig = ctx.measureText('test');
			const bigFooterSize = (tmpSizeBig.actualBoundingBoxAscent + tmpSizeBig.actualBoundingBoxDescent + options.footer[0].padding[0]);
			const footerBoundsCalc = (smallFooterSize * (options.footer.length - options.bigRows.length)) + (bigFooterSize * options.bigRows.length);
			if (footerBoundsCalc < (height * 0.65))
			{
				extraBottomOffset = (height * 0.65) - footerBoundsCalc;
			}
		}

		const imageInset = this._parsePadding(0);

		if (options.header)
		{
			ctx.font = 'bold ' + `${ printStyle === 'wide' ? ctxFont*2.5 : ctxFont }` + 'px "Noto Sans Mono", monospace';
			for (const headNote of options.header)
			{
				const padding = this._parsePadding(headNote.padding);
				const tmpInsets = ctx.measureText(sanitizeText(headNote.text));
				console.log('header insets', tmpInsets);

				imageInset.top += tmpInsets.actualBoundingBoxAscent + tmpInsets.actualBoundingBoxDescent + padding.top + padding.bottom;

				ctx.fillStyle = 'black';

				ctx.fillText(
					sanitizeText(headNote.text),
					printStyle === 'wide' && !this.customerLogo ? padding.left : width / 2 - tmpInsets.width / 2,
					imageInset.top - padding.bottom,
				);
			}
		}
		if (this.customerLogo && printStyle === 'wide')
		{
			ctx.drawImage(this.customerLogo, 0, 0, width / 4, (width / 4) * (this.customerLogo.height / this.customerLogo.width));
		}
		if (printStyle === 'wide')
		{
			ctx.font = 'bold ' + (ctxFont * 0.75) + 'px "Noto Sans Mono", monospace';
			ctx.fillText(
				'Print Date: ' + this.pict.fable.Dates.dayJS(new Date().toISOString()).format('MM/DD/YYYY'),
				(width / 5) * 3,
				height,
			);
		}

		if (options.footer)
		{
			options.footer = options.footer.reverse();
			let currentOffset = height - extraBottomOffset;
			ctx.font = 'bold ' + ctxFont + 'px "Noto Sans Mono", monospace';
			for (const footNote of options.footer)
			{
				const tokens = footNote.text.split('N/A');
				let paddedPlaceholder = '';
				for (let x = 0; x < tokens.length; x++)
				{
					const tempSpacer = ('').padEnd(tokens[x].length);
					paddedPlaceholder = paddedPlaceholder.concat(tempSpacer);
					if ((x + 1) < tokens.length)
					{
						paddedPlaceholder = paddedPlaceholder.concat('N/A');
					}
				}
				footNote.text = tokens.join('   ');
				const bigRow = options.bigRows?.filter((r)=>
				{
					return footNote.text.startsWith(r);
				})?.length;
				let tmpFont = 'bold ' + ctxFont + 'px "Noto Sans Mono", monospace';
				if (bigRow)
				{
					tmpFont = 'bold ' + (ctxFont * 1.5) + 'px "Noto Sans Mono", monospace';
				}
				ctx.font = tmpFont;
				const padding = this._parsePadding(footNote.padding);
				const tmpInsets = ctx.measureText(sanitizeText(footNote.text));
				console.log('footer insets', tmpInsets);

				const tmpTextOffset = tmpInsets.actualBoundingBoxAscent + tmpInsets.actualBoundingBoxDescent + padding.top + padding.bottom;
				imageInset.bottom += tmpTextOffset;
				currentOffset -= tmpInsets.actualBoundingBoxDescent;
				// Boxes which are helpful for debugging painting issues
				/*ctx.strokeStyle = 'green';
				ctx.strokeRect(
					width / 2 - tmpInsets.width / 2,
					height - footNote.height,
					tmpInsets.width,
					tmpInsets.actualBoundingBoxAscent + tmpInsets.actualBoundingBoxDescent);

				ctx.strokeStyle = 'red';
				ctx.strokeRect(0, height - footNote.height, width, footNote.height);*/

				let fontColor = 'black';
				if (footNote.text.includes('COLORBEGIN'))
				{
					fontColor = footNote.text.split('COLORBEGIN')[1].split('COLOREND')[0];
				}
				let imageWidth = 0;
				if (footNote.text.includes('HLLOGO'))
				{
					imageWidth = (tmpTextOffset - padding.top + padding.bottom) * (this.headlightLogo.width / this.headlightLogo.height);
					ctx.drawImage(this.headlightLogo, 0, currentOffset - tmpInsets.actualBoundingBoxAscent, imageWidth, tmpTextOffset - padding.top - padding.bottom);
				}
				ctx.fillStyle = fontColor;
				ctx.fillText(
					sanitizeText(footNote.text),
					imageWidth ? imageWidth : (printStyle === 'wide' ? padding.left : width / 2 - tmpInsets.width / 2),
					currentOffset,
				);
				ctx.fillStyle = 'grey';
				ctx.fillText(
					paddedPlaceholder,
					imageWidth ? imageWidth : (printStyle === 'wide' ? padding.left : width / 2 - tmpInsets.width / 2),
					currentOffset,
				);
				currentOffset -= tmpInsets.actualBoundingBoxAscent + padding.top + padding.bottom;
				if (printStyle === 'wide')
				{
					for (let x = 0; x < 10; x++)
					{
						ctx.moveTo(0, currentOffset + padding.top/2 + padding.bottom + x);
						ctx.lineTo(options.previewID ? canvas.width : canvas.height, currentOffset + padding.top/2 + padding.bottom + x);
						ctx.stroke();
					}
				}
			}
		}

		ctx.fillStyle = 'black';

		const availableHeight = printStyle === 'wide' ? height - imageInset.bottom : height - imageInset.top - imageInset.bottom;
		const availableWidth = width - imageInset.left - imageInset.right;

		let boundingRectSize = Math.min(availableWidth, availableHeight);
		if (printStyle === 'wide' && boundingRectSize > (height * 0.3) && textSet.length)
		{
			boundingRectSize = height * 0.3;
		}

		console.log('Done', imageInset);
		// ctx.rect(imageInset.left, imageInset.top, width-imageInset.left-imageInset.right, height-imageInset.top-imageInset.bottom);
		// ctx.stroke();
		for (const child of [ ...qrCodeContainer.children ])
		{
			qrCodeContainer.removeChild(child);
		}
		console.log('boundingRectSize', boundingRectSize);
		new window.QRCode(qrCodeContainer, {
			text: options.text,
			width: boundingRectSize,
			height: boundingRectSize,
			colorDark: '#000000',
			colorLight: '#ffffff',
			correctLevel: window.QRCode.CorrectLevel.H,
		}).makeCode(options.text);
		const qrCodeCanvas = qrCodeContainer.querySelector('canvas');

		// console.log('image', qrCodeCanvas.toDataURL());
		ctx.drawImage(
			qrCodeCanvas,
			// shift to middle of bounding box
			printStyle === 'wide' ? imageInset.left + (availableWidth - boundingRectSize) : imageInset.left + (availableWidth - boundingRectSize) / 2,
			printStyle === 'wide' ? 0 : imageInset.top + (availableHeight - boundingRectSize) / 2,
			boundingRectSize,
			boundingRectSize,
		);
		ctx.restore();

		return canvas.toDataURL('image/jpeg', .7);
	}

	/* -- Utility Methods ---------------------------------------- */

	/**
	* Get the layers used for generating QR codes and printing them.
	* @return {{canvas: HTMLCanvasElement, qrCodeContainer: HTMLDivElement, printFrame: HTMLIFrameElement, hiddenLayer: HTMLDivElement}}
	*/
	getGenerationLayers()
	{
		if (!this._hiddenLayer)
		{
			this._hiddenLayer = document.createElement('div');

			this._hiddenLayer.style.visibility = 'hidden';
			this._hiddenLayer.style.height = '0';
			this._hiddenLayer.style.width = '0';
			this._hiddenLayer.style.position = 'absolute';
			this._hiddenLayer.style.top = '-10000px';
			this._hiddenLayer.style.left = '-10000px';

			this._qrCodeContainer = document.createElement('div');
			this._qrCodeContainer.style.display = 'none';
			this._hiddenLayer.appendChild(this._qrCodeContainer);

			this._canvas = document.createElement('canvas');
			this._hiddenLayer.appendChild(this._canvas);

			this._printFrame = document.createElement('iframe');
			this._hiddenLayer.appendChild(this._printFrame);

			document.body.appendChild(this._hiddenLayer);
		}

		return {
			hiddenLayer: this._hiddenLayer,
			canvas: this._canvas,
			qrCodeContainer: this._qrCodeContainer,
			printFrame: this._printFrame,
		};
	}
}

module.exports = QRCodeProvider;
module.exports.default_configuration = {};
